import { createSlice } from '@reduxjs/toolkit';
import { setUpdateTodo } from './todoListSlice';
import { setDeleteData as deleteOim, addMainForm as addOim } from './oim-mainUpdateSlice';
import { setDeleteData as deleteOex, addMainForm as addOex } from './oex-mainUpdateSlice';
import { setDeleteData as deleteAim, addMainForm as addAim } from './aim-mainUpdateSlice';
import { setDeleteData as deleteAex, addMainForm as addAex } from './aex-mainUpdateSlice';
import { setDeleteData as deleteTrucking, addMainForm as addTrucking } from './trucking-mainUpdateSlice';

/** Todo state of the Shipments screen is kept per shipment, keyed by tab and main row id. */
export const shipmentTodoKey = (tab, parentId) => `${tab}:${parentId}`;
const cardKey = (card) => shipmentTodoKey(card.shipment_type, card.parent_id);
const initialState = { panel: [], summaries: {}, details: {}, revision: 0, changedAt: {}, panelChangedAt: {},
    removed: {}, versions: {}, floorVersion: 0 };

/** Records that a shipment changed after now, so slower READ replies cannot overwrite it. */
const markChanged = (state, key) => {
    state.revision += 1;
    state.changedAt[key] = state.revision;
};

/**
 * A removed shipment that a later READ returns again is back on screen, so its pushes are
 * accepted again. Shipments missing from the reply stay blocked; otherwise a late push
 * would bring back the cards of a deleted shipment.
 */
const settleRemovals = (state, keys, startedRevision) => {
    keys.forEach((key) => {
        if (state.removed[key] && !(state.changedAt[key] > startedRevision)) delete state.removed[key];
    });
};

const slice = createSlice({
    name: 'shipmentTodo',
    initialState,
    reducers: {
        /** Side panel cards on entering Shipments; shipments pushed during the read keep their newer cards. */
        panelReadReceived(state, { payload: { cards, startedRevision } }) {
            const changed = new Set([...Object.keys(state.changedAt), ...Object.keys(state.panelChangedAt)].filter((key) =>
                Math.max(state.changedAt[key] ?? 0, state.panelChangedAt[key] ?? 0) > startedRevision));
            state.panel = [
                ...cards.filter((card) => card.display_panel === true && !changed.has(cardKey(card))),
                ...state.panel.filter((card) => changed.has(cardKey(card))),
            ];
            settleRemovals(state, new Set(cards.map(cardKey)), startedRevision);
        },
        /** Grid badge counts of the rows just read; an In Progress read replaces the whole tab. */
        summariesReadReceived(state, { payload: { tab, parentIds, summaries, startedRevision, replaceTab } }) {
            const requested = new Set(parentIds.map((id) => shipmentTodoKey(tab, id)));
            Object.keys(state.summaries).forEach((key) => {
                if ((requested.has(key) || (replaceTab && key.startsWith(`${tab}:`))) &&
                    !(state.changedAt[key] > startedRevision)) delete state.summaries[key];
            });
            // Rows with only comment cards have no counts but are still back on screen.
            settleRemovals(state, requested, startedRevision);
            summaries.forEach((row) => {
                const key = cardKey(row);
                if (!(state.changedAt[key] > startedRevision)) state.summaries[key] = row.keys;
            });
        },
        /** A detail panel started reading its shipment's cards on this connection. */
        detailReadStarted(state, { payload: { key, epoch } }) {
            state.details[key] = { ...state.details[key], pending: true, requestEpoch: epoch };
        },
        /** Detail cards arrived; replies from an older connection or older than a push are ignored. */
        detailReadReceived(state, { payload: { key, cards, epoch, startedRevision } }) {
            const entry = state.details[key];
            if (entry?.requestEpoch !== epoch) return;
            entry.pending = false;
            if (state.changedAt[key] > startedRevision) return;
            state.details[key] = { cards, epoch, pending: false, stale: false };
        },
        /** The detail read failed; the panel keeps its last cards and may read again. */
        detailReadFailed(state, { payload: { key, epoch } }) {
            if (state.details[key]?.requestEpoch === epoch) {
                state.details[key].pending = false;
                state.details[key].failed = true;
            }
        },
        /**
         * The server sent one shipment's current badges and panel cards after a change.
         * Older pushes and pushes for removed shipments are ignored; an open detail re-reads.
         */
        replaceShipmentTodo(state, { payload }) {
            const key = cardKey(payload);
            if (state.removed[key]) return;
            if (payload.version != null && payload.version <= Math.max(state.floorVersion, state.versions[key] ?? 0)) return;
            if (payload.version != null) state.versions[key] = payload.version;
            state.panel = [...state.panel.filter((card) => cardKey(card) !== key),
                ...payload.panel.filter((card) => card.display_panel === true)];
            state.summaries[key] = payload.summary;
            if (state.details[key]) {
                state.details[key].stale = true;
                state.details[key].failed = false;
            }
            markChanged(state, key);
        },
        /**
         * Midnight refresh: yesterday's panel cards leave and today's appear for every shipment.
         * Shipments pushed after the reset was built keep their newer state.
         */
        resetShipmentTodo(state, { payload }) {
            if (payload.version != null && payload.version <= state.floorVersion) return;
            const newer = new Set(Object.keys(state.versions).filter((key) =>
                payload.version != null && state.versions[key] > payload.version));
            const oldPanelKeys = state.panel.map(cardKey);
            state.panel = [...payload.panel.filter((row) => !newer.has(cardKey(row)) && !state.removed[cardKey(row)]),
                ...state.panel.filter((row) => newer.has(cardKey(row)))];
            const summaries = {};
            payload.summaries.forEach((row) => { if (!state.removed[cardKey(row)]) summaries[cardKey(row)] = row.keys; });
            newer.forEach((key) => { summaries[key] = state.summaries[key]; });
            const keys = new Set([...Object.keys(state.summaries), ...Object.keys(summaries), ...Object.keys(state.details),
                ...oldPanelKeys, ...state.panel.map(cardKey)]);
            state.summaries = summaries;
            keys.forEach((key) => {
                if (newer.has(key)) return;
                markChanged(state, key);
                if (state.details[key]) { state.details[key].stale = true; state.details[key].failed = false; }
            });
            if (payload.version != null) state.floorVersion = payload.version;
        },
    },
    extraReducers: (builder) => {
        // A shipment added back to the grid (for example after a team transfer) accepts pushes again.
        [addOim, addOex, addAim, addAex, addTrucking].forEach((action, tab) => {
            builder.addCase(action, (state, { payload }) => {
                (Array.isArray(payload) ? payload : [payload]).forEach((row) => {
                    delete state.removed[shipmentTodoKey(tab, row.id)];
                });
            });
        });
        // A shipment removed from the grid drops its cards everywhere and ignores late pushes.
        [deleteOim, deleteOex, deleteAim, deleteAex, deleteTrucking].forEach((action, tab) => {
            builder.addCase(action, (state, { payload: parentId }) => {
                const key = shipmentTodoKey(tab, parentId);
                state.panel = state.panel.filter((card) => cardKey(card) !== key);
                delete state.summaries[key];
                delete state.details[key];
                state.removed[key] = true;
                markChanged(state, key);
            });
        });
        builder.addCase(setUpdateTodo, (state, { payload }) => {
            // Marking a card read shows at once. Which cards are listed, and their colors,
            // come only from server pushes.
            const reads = new Map(payload.filter((row) => typeof row.is_read === 'boolean').map((row) => [row.id, row.is_read]));
            state.panel.forEach((card) => {
                if (reads.has(card.id) && card.is_read !== reads.get(card.id)) {
                    card.is_read = reads.get(card.id);
                    state.revision += 1;
                    state.panelChangedAt[cardKey(card)] = state.revision;
                }
            });
        });
    },
});

export const { panelReadReceived, summariesReadReceived, detailReadStarted, detailReadReceived,
    detailReadFailed, replaceShipmentTodo, resetShipmentTodo } = slice.actions;

/** Turns a grid read reply into badge counts for the rows the viewer may see. */
export const gridSummariesReceived = ({ tab, receivedData, masterTable, startedRevision, isComplete }) =>
    summariesReadReceived({
        tab,
        parentIds: (receivedData[masterTable] ?? []).map((row) => row.id),
        summaries: receivedData.shipment_todo_summary ?? [],
        startedRevision,
        replaceTab: !isComplete,
    });
export default slice.reducer;
